// @ts-strict-ignore
import {immutableSplice} from 'common/util/array';

import {SidenavSection, SidenavSectionItem} from './types';

/*
 * Get an updated copy of item after inserting itemToInsert nested under the item with parentValue
 */
export function getSidenavItemWithInsertion(
  item: SidenavSectionItem,
  itemToInsert: SidenavSectionItem,
  parentValue: string,
): SidenavSectionItem {
  if (parentValue === item.value) {
    return {
      ...item,
      nestedItems: [...(item.nestedItems || []), itemToInsert].sort((itemA, itemB) => {
        return itemA.value < itemB.value ? -1 : 1;
      }),
    };
  }

  if (item.nestedItems) {
    const updatedChildItems = item.nestedItems.map((childItem) =>
      getSidenavItemWithInsertion(childItem, itemToInsert, parentValue),
    );

    return {...item, nestedItems: updatedChildItems};
  }
  return item;
}

/*
 * Get an updated copy of the sidenavItem after removing all the items with `item.value = value`
 */
export function getSidenavItemWithoutValue(
  value: string,
  item: SidenavSectionItem,
): {updatedItem?: SidenavSectionItem; removedChildren: SidenavSectionItem[]} {
  if (value === item.value) {
    return {
      updatedItem: undefined,
      removedChildren: item.nestedItems || [],
    };
  }

  if (item.nestedItems) {
    const updatedAndRemovedItems = item.nestedItems.map((childItem) => getSidenavItemWithoutValue(value, childItem));

    const updatedNestedItems = updatedAndRemovedItems
      .filter(({updatedItem}) => Boolean(updatedItem) && updatedItem.value !== value)
      .map(({updatedItem}) => updatedItem);
    const removedChildren = updatedAndRemovedItems
      .map((updatedAndRemovedItem) => updatedAndRemovedItem.removedChildren)
      .flat();

    return {updatedItem: {...item, nestedItems: updatedNestedItems}, removedChildren};
  }
  return {
    updatedItem: item,
    removedChildren: [],
  };
}

export function sidenavItemSplice<T extends SidenavSection>(
  sections: T[],
  sectionIndex: number,
  ...args: [itemIndex?: number, numToDelete?: number, itemToInsert?: SidenavSectionItem]
): T[] {
  return immutableSplice(sections, sectionIndex, 1, {
    ...sections[sectionIndex],
    items: immutableSplice(sections[sectionIndex].items, ...args),
  });
}
